// PAST THE SCHOOL'S ALLOWANCE: THE RED NOTE AND THE TICK BOX. (ZEN-842)
//
// One piece, used by the withdrawal form and the "Tie eligible expenses"
// panel, and meant for the expense window too (ZEN-838). The words live only
// in lib/aboveAllowance.js; the figure comes only from the server, in the
// refusal of a save past the allowance — so the amount the family ticks is the
// amount the server asks for.
//
// Maryse, ZEN-842 session, 3 Nov 2026: a tick box, and "the save can only work
// if the box is ticked"; red "when it is truly above"; nothing at all "when we
// are still within COA, when mom paid and is not seeking reimbursement"; and a
// tick counts only for the amount it was given for (detail 2, "agreed").
import React from "react";
import { WarningCircle } from "@phosphor-icons/react";
import { pastAllowanceWords } from "@/lib/aboveAllowance";
import { TIE_MIN_USD } from "@/lib/withdrawalHeadroom";

const NONE = { at_risk_usd: 0, acknowledged: true };

/** The figure a refused save carries (code past_allowance_tick_needed), or null. */
export function riskFromRefusal(err) {
  const d = err?.response?.data?.detail;
  if (d && typeof d === "object" && d.code === "past_allowance_tick_needed" && Number(d.at_risk_usd) > 0) {
    return { at_risk_usd: Number(d.at_risk_usd), schools: [], acknowledged: false };
  }
  return null;
}

/** The figure the last held save carried, for this selection.
 *
 *  Maryse, 4 Nov 2026, after review round 7: the note shows when Save is
 *  pressed ("Yes, on Save"). So the screen asks the server nothing before the
 *  save, and no dollar amount travels in a web address (addresses end up in
 *  logs). Pressing Save past the allowance is refused with the figure; hand
 *  that error to noteRefusal and the box appears with it, to tick and save
 *  again.
 *
 *  `selection` is what is ticked (ids and amounts, the dates, the amount), or
 *  null while the window is closed. It is never sent anywhere. The figure AND
 *  the tick belong to it: the moment it changes — a row ticked or unticked, an
 *  amount or date edited, the window closed — both are thrown away, so going
 *  back to the same selection, or reopening the window, shows nothing until
 *  the next Save asks the server afresh (review round 8).
 *  Returns [risk, noteRefusal, tick, setTick]. */
const FORGOTTEN = { key: null, risk: null, tick: null };
export function useAllowanceRefusal(selection) {
  const key = selection ? JSON.stringify(selection) : null;
  const [held, setHeld] = React.useState(FORGOTTEN);
  // Forget, not hide: a stale entry is cleared at once (React re-renders
  // straight away), so it cannot come back if the old selection returns.
  if (held.key !== null && held.key !== key) setHeld(FORGOTTEN);
  const mine = held.key !== null && held.key === key;
  const risk = (mine && held.risk) || NONE;
  const tick = mine ? held.tick : null;
  const noteRefusal = React.useCallback((err) => {
    const r = riskFromRefusal(err);
    if (r && key) setHeld({ key, risk: r, tick: null });
    return !!r;
  }, [key]);
  const setTick = React.useCallback((v) => {
    setHeld((h) => (h.key !== null && h.key === key ? { ...h, tick: v } : h));
  }, [key]);
  return [risk, noteRefusal, tick, setTick];
}

/** Is Save allowed? Nothing past the allowance, a tick already on record that
 *  covers it, or a tick given for exactly this figure. */
export function tickCovers(risk, tickedFor) {
  const usd = Number(risk?.at_risk_usd) || 0;
  if (usd <= TIE_MIN_USD || risk?.acknowledged) return true;
  return tickedFor != null && Math.abs(Number(tickedFor) - usd) <= TIE_MIN_USD;
}

/** The figure to send as `allowance_ack_usd`, or null. */
export function ackToSend(risk, tickedFor) {
  const usd = Number(risk?.at_risk_usd) || 0;
  return usd > TIE_MIN_USD && tickedFor != null && Math.abs(Number(tickedFor) - usd) <= TIE_MIN_USD ? usd : null;
}

export function PastAllowanceTick({ risk, tickedFor, onTick, firstMention = true, testid = "wd-past-allowance" }) {
  const usd = Number(risk?.at_risk_usd) || 0;
  if (usd <= TIE_MIN_USD) return null;
  const words = pastAllowanceWords(usd, { firstMention });
  const ticked = !!risk?.acknowledged || tickCovers(risk, tickedFor);
  return (
    <div className="mt-3 rounded-lg border border-[#F01919] bg-[#F01919]/5 p-3" data-testid={testid}>
      <div className="flex items-start gap-2 text-sm font-bold text-[#B91C1C]">
        <WarningCircle size={16} weight="fill" className="mt-0.5 shrink-0" />
        <span data-testid={`${testid}-note`}>{words.note}</span>
      </div>
      <label className="mt-2 flex items-start gap-2 text-xs font-semibold text-[#7F1D1D]">
        <input type="checkbox" className="mt-0.5" checked={ticked}
          disabled={!!risk?.acknowledged}
          onChange={(e) => onTick(e.target.checked ? usd : null)}
          data-testid={`${testid}-tick`} />
        <span>{words.tick}</span>
      </label>
    </div>
  );
}
